import { useTheme } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { PromoBannerSlot } from '@onesource/components';
import type { TLessonBase } from '@onesource/schemas';
import { LessonStatus, PromoBannerPosition, SpaceStatus } from '@onesource/schemas';
import {
  useGetCategories,
  useGetLessons,
  useGetModuleProgress,
  useGetSpace,
  useGetSubLessons,
} from '@onesource/services';
import { stripHtml } from '@onesource/utils';
import { useNavigate } from '@tanstack/react-router';
import DOMPurify from 'dompurify';
import { enqueueSnackbar } from 'notistack';
import { type FC, useEffect } from 'react';
import { DraftBadge } from 'src/components/draft-badge';

import { useContentRoute } from '../hooks/use-content-route';
import { usePreviewMode } from '../hooks/use-preview-mode';
import { ModuleDetailSkeleton } from './module-detail-skeleton';
import { ModuleSectionCard } from './module-section-card';
import { NavBreadcrumbCurrent, NavBreadcrumbLabel, NavBreadcrumbLink, NavBreadcrumbs } from './nav-breadcrumbs';
import { StickyNavRow } from './sticky-nav-row';

interface SectionLoaderProps {
  section: TLessonBase;
  index: number;
  parentSpaceId: string;
  moduleId: string;
  expandedSectionId?: string;
  showSectionLabel?: boolean;
  showLessonLabel?: boolean;
}

/** Loads sub-lessons + categories for a single accordion section. */
const SectionLoader = ({
  section,
  index,
  parentSpaceId,
  moduleId,
  expandedSectionId,
  showSectionLabel,
  showLessonLabel,
}: SectionLoaderProps) => {
  const { data: subLessons, isLoading: isLoadingSubLessons } = useGetSubLessons(section.id);
  const { data: categories, isLoading: isLoadingCategories } = useGetCategories(section.id);
  const { isPreview } = usePreviewMode();
  const isLoading = isLoadingSubLessons || isLoadingCategories;

  const visibleSubLessons = isPreview
    ? subLessons || []
    : subLessons?.filter(l => l.status === LessonStatus.PUBLISHED) || [];

  return (
    <ModuleSectionCard
      section={section}
      index={index}
      subLessons={isLoading ? [] : visibleSubLessons}
      categories={isLoading ? [] : categories || []}
      parentSpaceId={parentSpaceId}
      moduleId={moduleId}
      expandedSectionId={expandedSectionId}
      showSectionLabel={showSectionLabel}
      showLessonLabel={showLessonLabel}
    />
  );
};

/**
 * Module (CONTENT sub-space) detail: header, progress, description and the
 * accordion of sections. Renders under the slug route `/{program}/{module}`;
 * ids come from the resolved content-route context.
 */
export const ModuleDetailView: FC<{ expandedSectionId?: string }> = ({ expandedSectionId }) => {
  const { programId: parentSpaceId, programSlug, subSpaceId: moduleId = '' } = useContentRoute();
  const { isPreview, canPreview } = usePreviewMode();
  const theme = useTheme();
  const navigate = useNavigate();
  const { data: parentSpace, isLoading: isLoadingParent, error: parentError } = useGetSpace(parentSpaceId);
  const { data: module, isLoading: isLoadingModule, error: moduleError } = useGetSpace(moduleId);
  const { data: lessons, isLoading: isLoadingLessons, error: lessonsError } = useGetLessons(moduleId);
  const { data: moduleProgress } = useGetModuleProgress(moduleId);

  // The API gates a locked module's content (403 MODULE_LOCKED). Bounce back to
  // the program home with a message instead of a generic error.
  useEffect(() => {
    const data = (lessonsError as { response?: { data?: { code?: string } } } | null)?.response?.data;
    if (data?.code === 'MODULE_LOCKED') {
      enqueueSnackbar('Complete the prerequisite module to unlock this one.', { variant: 'info' });
      navigate({ to: '/programs/$programSlug', params: { programSlug } });
    }
  }, [lessonsError, navigate, programSlug]);

  const isLoading = isLoadingParent || isLoadingModule || isLoadingLessons;

  if (isLoading) {
    return <ModuleDetailSkeleton />;
  }

  if (parentError || moduleError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load module: {parentError?.message || moduleError?.message}</Alert>
      </Box>
    );
  }

  if (!parentSpace || !module) {
    return (
      <Box p={3}>
        <Alert severity="error">Module not found</Alert>
      </Box>
    );
  }

  // A draft module (or one under a draft container) isn't live for members. The
  // API still serves it to the app's managers, so gate it on the preview toggle
  // here — with preview off a manager sees exactly what a member sees.
  if (!isPreview && (module.status === SpaceStatus.DRAFT || parentSpace.status === SpaceStatus.DRAFT)) {
    return (
      <Box p={3}>
        {canPreview ? (
          <Alert severity="info">
            This module is unpublished. Turn on “Preview unpublished content” above to view it.
          </Alert>
        ) : (
          <Alert severity="error">Module not found</Alert>
        )}
      </Box>
    );
  }

  const progressPercent = moduleProgress?.progressPercent ?? 0;
  const visibleSections = isPreview ? lessons || [] : lessons?.filter(l => l.status === LessonStatus.PUBLISHED) || [];
  const sanitizedDescription = module.description ? DOMPurify.sanitize(module.description) : '';
  const sanitizedObjectives = module.learningObjectives ? DOMPurify.sanitize(module.learningObjectives) : '';
  const hasTwoColumns = !!sanitizedDescription && !!sanitizedObjectives;

  return (
    // No `overflow` here: it would make this box the sticky containing block
    // and stop the nav row from pinning to the viewport.
    <Box sx={{ minHeight: 'calc(100vh - 180px)' }}>
      {/* Sticky nav — breadcrumb on desktop, back to the program on mobile */}
      <StickyNavRow backHref={`/programs/${programSlug}`} backLabel={stripHtml(parentSpace.name)}>
        <NavBreadcrumbs>
          <NavBreadcrumbLink to="/programs/$programSlug" params={{ programSlug }}>
            <NavBreadcrumbLabel html={parentSpace.name} />
          </NavBreadcrumbLink>
          <NavBreadcrumbCurrent>
            <NavBreadcrumbLabel html={module.name} />
            {isPreview && module.status === SpaceStatus.DRAFT && <DraftBadge />}
          </NavBreadcrumbCurrent>
        </NavBreadcrumbs>
      </StickyNavRow>

      <Box sx={{ pb: 2 }}>
        <PromoBannerSlot
          sx={{ width: { xs: '100%', md: 'calc(100% - 205px)' } }}
          entityId={moduleId}
          position={PromoBannerPosition.HEADER}
        />

        {module.showProgressBar !== false && (
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              maxWidth: { xs: '100%', md: 'calc(100% - 205px)' },
            }}
          >
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: 16.6,
                lineHeight: '24px',
                color: '#2e2e2d',
              }}
            >
              {progressPercent}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{
                width: '100%',
                height: 10,
                borderRadius: 3,
                bgcolor: 'rgba(0, 0, 0, 0.1)',
                '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: '#c45569' },
              }}
            />
          </Box>
        )}

        {(sanitizedDescription || sanitizedObjectives) && (
          <Box
            sx={{
              // The objectives sit beside the description on a wide screen and
              // stack under it on a phone, where a half-width column of prose is
              // unreadable. The breakpoint belongs here rather than to whoever
              // wrote the copy: an inline grid in the description could not carry
              // one, and the sanitizer drops any <style> block.
              display: 'grid',
              gridTemplateColumns: hasTwoColumns ? { xs: '1fr', md: '1fr 1fr' } : '1fr',
              gap: { xs: 2, md: 4 },
              alignItems: 'start',
              // Two columns line up with the section list below (and the progress
              // bar above), so the block has the same right edge as everything
              // else on the page. A single column keeps its narrower measure.
              maxWidth: hasTwoColumns
                ? { xs: '100%', md: 'calc(100% - 205px)' }
                : { xs: '100%', md: 'calc(100% - 315px)' },
              fontFamily: '"Figtree", sans-serif',
              fontWeight: 400,
              fontSize: 14,
              lineHeight: '19px',
              color: 'black',
              mb: { xs: 2, md: 3 },
              '& strong, & b': { fontWeight: 700 },
              '& p': { margin: 0, marginBottom: '8px', '&:last-child': { marginBottom: 0 } },
              '& ol': { listStyle: 'revert', pl: { xs: 3, md: 5 }, margin: 0 },
              '& ul': { listStyle: 'revert', pl: { xs: 3, md: 5 } },
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                fontWeight: 700,
                lineHeight: 1.25,
                margin: '0 0 8px',
                fontSize: 16,
              },
            }}
          >
            {sanitizedDescription && <Box dangerouslySetInnerHTML={{ __html: sanitizedDescription }} />}
            {sanitizedObjectives && <Box dangerouslySetInnerHTML={{ __html: sanitizedObjectives }} />}
          </Box>
        )}
      </Box>

      <PromoBannerSlot
        entityId={moduleId}
        position={PromoBannerPosition.MIDDLE}
        sx={{ width: { xs: '100%', md: 'calc(100% - 205px)' } }}
      />

      <Box sx={{ height: '100%', maxWidth: { xs: '100%', md: 'calc(100% - 205px)' } }}>
        {visibleSections.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '14px', md: '17px' } }}>
            {visibleSections.map((section, index) => (
              <SectionLoader
                key={section.id}
                section={section}
                index={index + 1}
                parentSpaceId={parentSpaceId}
                moduleId={moduleId}
                expandedSectionId={expandedSectionId}
                showSectionLabel={module.showSectionLabel !== false}
                showLessonLabel={module.showLessonLabel !== false}
              />
            ))}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 6, bgcolor: 'white', borderRadius: '10px' }}>
            <Typography
              sx={{ fontFamily: '"Figtree", sans-serif', fontWeight: 400, fontSize: 14, color: 'rgba(0, 0, 0, 0.6)' }}
            >
              No sections available in this module.
            </Typography>
          </Box>
        )}
      </Box>

      <PromoBannerSlot
        entityId={moduleId}
        position={PromoBannerPosition.FOOTER}
        sx={{
          width: { xs: '100%', md: 'calc(100% - 205px)' },
          position: { xs: 'sticky', md: 'static' },
          bottom: { xs: 16, md: 'auto' },
          zIndex: { xs: theme.zIndex.appBar, md: 'auto' },
        }}
      />
    </Box>
  );
};
